import React, { useEffect, useRef, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillLeave,
} from "@ionic/react";
import {
  saveOutline,
  checkmarkDoneOutline,
  checkmarkCircle,
  refreshOutline,
  timeOutline,
  arrowBackOutline,
  closeOutline,
  homeOutline,
  mailOutline,
} from "ionicons/icons";
import { useHistory } from "react-router-dom";

import Header from "src/components/Header";
import {
  obtenerCatalogoOrdenTrabajo,
  buscarPuntosServicioAsignados,
  iniciarOrdenTrabajo,
  guardarBorradorOrdenTrabajo,
  finalizarOrdenTrabajo,
  listarHistorialPorPs,
  obtenerOrdenTrabajo,
  OrdenTrabajoCatalogo,
  OrdenTrabajoDetalle,
  OrdenTrabajoPayload,
  OrdenTrabajoHistorialItem,
  PuntoServicioAsignado,
  MultiOpcion,
} from "src/services/ordenTrabajo";

interface Toast {
  show: boolean;
  msg: string;
  color?: string;
}

/** Datos que se muestran en la pantalla de confirmación al finalizar la OT. */
interface ConfirmacionOT {
  numero: string;
  psNombre: string;
  psCodigo: string;
  fecha: string;
  finalizadoEn: string;
  correoCliente: boolean;
}

type Opcion = { codigo: string; etiqueta: string };

const codigosDeMulti = (lista: MultiOpcion[] | undefined): string[] =>
  (lista || []).map((m) => m.codigo);

const OrdenTrabajo: React.FC = () => {
  const history = useHistory();
  const [cargandoCatalogo, setCargandoCatalogo] = useState(true);
  const [catalogo, setCatalogo] = useState<OrdenTrabajoCatalogo | null>(null);
  const [toast, setToast] = useState<Toast>({ show: false, msg: "" });

  // Buscador de PS
  const [psBusqueda, setPsBusqueda] = useState("");
  const [psResultados, setPsResultados] = useState<PuntoServicioAsignado[]>([]);
  const [psBuscando, setPsBuscando] = useState(false);

  // Orden de trabajo activa (borrador o finalizada)
  const [orden, setOrden] = useState<OrdenTrabajoDetalle | null>(null);
  const [iniciando, setIniciando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  // Confirmación mostrada al finalizar (reemplaza al formulario hasta que el
  // usuario decida cargar otra orden o volver al inicio).
  const [confirmacion, setConfirmacion] = useState<ConfirmacionOT | null>(null);

  // Campos del formulario
  const [fecha, setFecha] = useState("");
  const [nombreContacto, setNombreContacto] = useState("");
  const [seFactura, setSeFactura] = useState("");
  const [trabajos, setTrabajos] = useState<string[]>([]);
  const [sectores, setSectores] = useState("");
  const [frecuencias, setFrecuencias] = useState<string[]>([]);
  const [conformidad, setConformidad] = useState<string>(""); // "SI" | "NO" | ""
  const [observaciones, setObservaciones] = useState("");

  // Firma (canvas)
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const [firmaHecha, setFirmaHecha] = useState(false);
  const [firmaGuardada, setFirmaGuardada] = useState<string | null>(null); // firma persistida
  const [modoFirma, setModoFirma] = useState<"ver" | "dibujar">("dibujar");

  // Historial por PS (modal)
  const [histOpen, setHistOpen] = useState(false);
  const [histPsBusqueda, setHistPsBusqueda] = useState("");
  const [histPsResultados, setHistPsResultados] = useState<PuntoServicioAsignado[]>([]);
  const [histBuscando, setHistBuscando] = useState(false);
  const [histPsSel, setHistPsSel] = useState<PuntoServicioAsignado | null>(null);
  const [histLista, setHistLista] = useState<OrdenTrabajoHistorialItem[]>([]);
  const [histCargando, setHistCargando] = useState(false);
  const [histDetalle, setHistDetalle] = useState<OrdenTrabajoDetalle | null>(null);
  const [histDetalleCargando, setHistDetalleCargando] = useState(false);

  const finalizada = orden?.estado === "FINALIZADA";
  const tieneFirma = firmaHecha || !!firmaGuardada;

  // ----- Carga inicial del catálogo -----
  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const cat = await obtenerCatalogoOrdenTrabajo();
        if (activo) setCatalogo(cat);
      } catch (error: any) {
        if (activo)
          setToast({ show: true, msg: error?.message || "No se pudo cargar el catálogo.", color: "danger" });
      } finally {
        if (activo) setCargandoCatalogo(false);
      }
    })();
    return () => {
      activo = false;
    };
  }, []);

  // ----- Buscador de PS (debounce) -----
  useEffect(() => {
    // Con una OT en curso (o con la confirmación en pantalla) el buscador no se
    // renderiza, así que no hay nada que buscar.
    if (orden || confirmacion) return;
    let activo = true;
    const termino = psBusqueda.trim();
    if (termino.length < 1) {
      setPsResultados([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setPsBuscando(true);
      try {
        const datos = await buscarPuntosServicioAsignados(termino);
        if (!activo) return;
        setPsResultados((datos || []).slice(0, 10));
      } catch {
        if (!activo) return;
        setPsResultados([]);
      } finally {
        if (activo) setPsBuscando(false);
      }
    }, 250);
    return () => {
      activo = false;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psBusqueda, orden, confirmacion]);

  // Rehidrata los campos del formulario desde una OT.
  const rehidratar = (ot: OrdenTrabajoDetalle) => {
    setOrden(ot);
    setFecha(ot.fecha || "");
    setNombreContacto(ot.nombre_contacto || "");
    setSeFactura(ot.se_factura || "");
    setSectores(ot.sectores || "");
    setObservaciones(ot.observaciones || "");
    setTrabajos(codigosDeMulti(ot.multi?.TRABAJOS_REALIZADOS));
    setFrecuencias(codigosDeMulti(ot.multi?.FRECUENCIAS));
    setConformidad(ot.conformidad === true ? "SI" : ot.conformidad === false ? "NO" : "");
    setFirmaHecha(false);
    const firma = ot.firma_base64 || null;
    setFirmaGuardada(firma);
    // Si ya hay una firma guardada, se muestra; si no, se habilita el canvas.
    setModoFirma(firma ? "ver" : "dibujar");
  };

  const seleccionarPS = async (ps: PuntoServicioAsignado) => {
    setPsResultados([]);
    setConfirmacion(null);
    setIniciando(true);
    try {
      const ot = await iniciarOrdenTrabajo(ps.id);
      rehidratar(ot);
      setToast({
        show: true,
        msg:
          ot?.estado === "BORRADOR" && (ot.se_factura || ot.sectores || (ot.multi?.TRABAJOS_REALIZADOS || []).length)
            ? "Se reanudó una orden pendiente de este PS."
            : "Orden de trabajo iniciada.",
        color: "success",
      });
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo iniciar la orden.", color: "danger" });
    } finally {
      setIniciando(false);
    }
  };

  const toggleMulti = (current: string[], codigo: string): string[] =>
    current.includes(codigo) ? current.filter((c) => c !== codigo) : [...current, codigo];

  const construirPayload = (): OrdenTrabajoPayload => ({
    fecha: fecha || null,
    nombre_contacto: nombreContacto.trim() || null,
    se_factura: seFactura || null,
    sectores: sectores.trim() || null,
    conformidad: conformidad === "SI" ? true : conformidad === "NO" ? false : null,
    observaciones: observaciones.trim() || null,
    trabajos_realizados: trabajos,
    frecuencias: frecuencias,
  });

  const guardarBorrador = async (silencioso = false) => {
    if (!orden || finalizada) return;
    setGuardando(true);
    try {
      const payload = construirPayload();
      // Si el usuario dibujó una firma nueva, la persistimos también en el borrador.
      if (firmaHecha) {
        const f = exportarFirma();
        if (f && f.length >= 50) payload.firma_b64 = f;
      }
      await guardarBorradorOrdenTrabajo(orden.id, payload);
      // Reflejar en el estado local que la firma quedó guardada.
      if (payload.firma_b64) {
        setFirmaGuardada(payload.firma_b64);
      }
      if (!silencioso)
        setToast({ show: true, msg: "Borrador guardado.", color: "success" });
    } catch (error: any) {
      if (!silencioso)
        setToast({ show: true, msg: error?.message || "No se pudo guardar el borrador.", color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  // ----- Firma (canvas) -----
  useEffect(() => {
    if (!orden || finalizada || modoFirma !== "dibujar") return;
    const initCanvas = () => {
      const cv = canvasRef.current;
      if (!cv) return;
      const rect = cv.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      cv.width = Math.max(1, Math.floor(rect.width * dpr));
      cv.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = cv.getContext("2d");
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = "#111";
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
      }
    };
    requestAnimationFrame(initCanvas);
    const t = window.setTimeout(initCanvas, 80);
    return () => window.clearTimeout(t);
  }, [orden, finalizada, modoFirma]);

  const beginDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    e.preventDefault();
    cv.setPointerCapture?.(e.pointerId);
    pointerIdRef.current = e.pointerId;
    isDrawingRef.current = true;
    lastPosRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const ctx = cv.getContext("2d");
    if (ctx) {
      ctx.beginPath();
      ctx.arc(lastPosRef.current.x, lastPosRef.current.y, 1.2, 0, Math.PI * 2);
      ctx.fillStyle = "#111";
      ctx.fill();
    }
    setFirmaHecha(true);
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const rect = cv.getBoundingClientRect();
    e.preventDefault();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const last = lastPosRef.current;
    if (!last) return;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPosRef.current = { x, y };
  };

  const endDraw = () => {
    const cv = canvasRef.current;
    if (cv) {
      try {
        if (pointerIdRef.current !== null) cv.releasePointerCapture?.(pointerIdRef.current);
      } catch {
        // noop
      }
    }
    isDrawingRef.current = false;
    lastPosRef.current = null;
    pointerIdRef.current = null;
  };

  const limpiarFirma = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, cv.width, cv.height);
    const rect = cv.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setFirmaHecha(false);
  };

  // Devuelve la firma vigente: la recién dibujada, o la guardada como fallback.
  const exportarFirma = (): string => {
    if (modoFirma === "dibujar" && firmaHecha) {
      const cv = canvasRef.current;
      if (cv) return cv.toDataURL("image/png");
    }
    return firmaGuardada || "";
  };

  const volverAFirmar = () => {
    setModoFirma("dibujar");
    setFirmaHecha(false);
  };

  const validar = (): string | null => {
    if (!fecha) return "Debe indicar la fecha del trabajo.";
    if (!seFactura) return 'Debe seleccionar "Se Factura".';
    if (trabajos.length === 0) return "Debe seleccionar al menos un trabajo realizado.";
    if (!conformidad) return "Debe indicar la conformidad (Sí / No).";
    if (!tieneFirma) return "Debe registrar la firma del cliente.";
    return null;
  };

  const finalizar = async () => {
    if (!orden) return;
    const err = validar();
    if (err) {
      setToast({ show: true, msg: err, color: "warning" });
      return;
    }
    const firma = exportarFirma();
    if (!firma || firma.length < 50) {
      setToast({ show: true, msg: "Debe registrar la firma del cliente.", color: "warning" });
      return;
    }
    setFinalizando(true);
    try {
      const resultado = await finalizarOrdenTrabajo(orden.id, {
        ...construirPayload(),
        firma_b64: firma,
      });
      const numero = resultado?.numero ?? orden.numero ?? orden.id;
      reiniciar();
      setConfirmacion({
        numero: String(numero),
        psNombre: resultado?.ps_nombre || orden.ps_nombre || "",
        psCodigo: resultado?.ps_codigo || orden.ps_codigo || "",
        fecha,
        finalizadoEn: resultado?.finalizado_en || "",
        correoCliente: !!resultado?.correo_cliente,
      });
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo finalizar la orden.", color: "danger" });
    } finally {
      setFinalizando(false);
    }
  };

  const reiniciar = () => {
    setOrden(null);
    setPsBusqueda("");
    setPsResultados([]);
    setFecha("");
    setNombreContacto("");
    setSeFactura("");
    setTrabajos([]);
    setSectores("");
    setFrecuencias([]);
    setConformidad("");
    setObservaciones("");
    setFirmaHecha(false);
    setFirmaGuardada(null);
    setModoFirma("dibujar");
  };

  // Autosave al salir de la vista si hay un borrador en curso.
  useIonViewWillLeave(() => {
    if (orden && !finalizada) {
      const payload = construirPayload();
      if (firmaHecha) {
        const f = exportarFirma();
        if (f && f.length >= 50) payload.firma_b64 = f;
      }
      guardarBorradorOrdenTrabajo(orden.id, payload).catch(() => undefined);
    }
  });

  // ----- Historial por PS (modal) -----
  useEffect(() => {
    if (!histOpen || histPsSel) return;
    let activo = true;
    const termino = histPsBusqueda.trim();
    if (termino.length < 1) {
      setHistPsResultados([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setHistBuscando(true);
      try {
        const datos = await buscarPuntosServicioAsignados(termino);
        if (!activo) return;
        setHistPsResultados((datos || []).slice(0, 10));
      } catch {
        if (!activo) return;
        setHistPsResultados([]);
      } finally {
        if (activo) setHistBuscando(false);
      }
    }, 250);
    return () => {
      activo = false;
      window.clearTimeout(t);
    };
  }, [histPsBusqueda, histOpen, histPsSel]);

  const abrirHistorial = () => {
    setHistOpen(true);
    setHistPsBusqueda("");
    setHistPsResultados([]);
    setHistPsSel(null);
    setHistLista([]);
    setHistDetalle(null);
  };

  const seleccionarPSHistorial = async (ps: PuntoServicioAsignado) => {
    setHistPsSel(ps);
    setHistPsResultados([]);
    setHistCargando(true);
    try {
      const lista = await listarHistorialPorPs(ps.id);
      setHistLista(lista || []);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cargar el historial.", color: "danger" });
      setHistLista([]);
    } finally {
      setHistCargando(false);
    }
  };

  const verDetalleHistorial = async (id: number) => {
    setHistDetalleCargando(true);
    try {
      const det = await obtenerOrdenTrabajo(id);
      setHistDetalle(det);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cargar la orden.", color: "danger" });
    } finally {
      setHistDetalleCargando(false);
    }
  };

  const etiqueta = (opciones: Opcion[] | undefined, codigo?: string | null): string => {
    if (!codigo) return "-";
    const o = (opciones || []).find((x) => x.codigo === codigo);
    return o ? o.etiqueta : codigo;
  };

  const etiquetasMulti = (opciones: Opcion[] | undefined, lista?: MultiOpcion[]): string => {
    const codigos = codigosDeMulti(lista);
    if (!codigos.length) return "-";
    return codigos.map((c) => etiqueta(opciones, c)).join(", ");
  };

  const renderCheckboxGroup = (
    titulo: string,
    seleccionados: string[],
    setSeleccionados: (v: string[]) => void,
    opciones: Opcion[]
  ) => (
    <>
      <IonItem lines="none">
        <IonLabel className="ion-text-wrap">{titulo}</IonLabel>
      </IonItem>
      <IonList>
        {opciones.map((o) => (
          <IonItem key={o.codigo}>
            <IonLabel className="ion-text-wrap">{o.etiqueta}</IonLabel>
            <IonCheckbox
              slot="start"
              checked={seleccionados.includes(o.codigo)}
              disabled={finalizada}
              onIonChange={() => setSeleccionados(toggleMulti(seleccionados, o.codigo))}
            />
          </IonItem>
        ))}
      </IonList>
    </>
  );

  return (
    <IonPage>
      <Header
        titulo="Orden de Trabajo"
        botonesDerecha={[{ icono: timeOutline, action: abrirHistorial }]}
      />
      <IonContent id="orden-trabajo" className="ion-padding">
        {cargandoCatalogo ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <IonSpinner name="crescent" />
          </div>
        ) : confirmacion ? (
          /* Confirmación de finalización: N° de orden generado + resumen */
          <div style={{ textAlign: "center", marginTop: 24 }}>
            {/* Bordó institucional (--ion-color-primary: #910434) */}
            <IonIcon
              icon={checkmarkCircle}
              color="primary"
              style={{ fontSize: 96 }}
            />
            <IonText>
              <h2 style={{ marginBottom: 4 }}>Orden de trabajo finalizada</h2>
            </IonText>
            <IonText color="medium">
              <p style={{ marginTop: 0 }}>
                Se registró correctamente el trabajo realizado.
              </p>
            </IonText>

            <IonCard>
              <IonCardContent style={{ textAlign: "center" }}>
                <IonText color="medium">
                  <p style={{ margin: 0, textTransform: "uppercase", fontSize: 12, letterSpacing: 1 }}>
                    N° de Orden de Trabajo
                  </p>
                </IonText>
                <IonText color="primary">
                  <h1 style={{ margin: "4px 0 12px 0", fontSize: 44, fontWeight: 700 }}>
                    {confirmacion.numero}
                  </h1>
                </IonText>
                <IonText>
                  <p style={{ margin: "2px 0" }} className="ion-text-wrap">
                    <strong>{confirmacion.psNombre}</strong>
                    {confirmacion.psCodigo ? ` (${confirmacion.psCodigo})` : ""}
                  </p>
                </IonText>
                {confirmacion.fecha && (
                  <IonText color="medium">
                    <p style={{ margin: "2px 0" }}>Fecha del trabajo: {confirmacion.fecha}</p>
                  </IonText>
                )}
                {confirmacion.finalizadoEn && (
                  <IonText color="medium">
                    <p style={{ margin: "2px 0" }}>Finalizada: {confirmacion.finalizadoEn}</p>
                  </IonText>
                )}
                {confirmacion.correoCliente && (
                  <IonText color="medium">
                    <p style={{ margin: "10px 0 0 0" }} className="ion-text-wrap">
                      <IonIcon icon={mailOutline} style={{ verticalAlign: "middle", marginRight: 4 }} />
                      Se envió el detalle al correo de contacto del cliente.
                    </p>
                  </IonText>
                )}
              </IonCardContent>
            </IonCard>

            <IonNote className="ion-text-wrap">
              Anotá el N° de orden: podés volver a consultarla desde el historial del punto de servicio.
            </IonNote>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "20px 0 24px 0" }}>
              <IonButton expand="block" onClick={() => setConfirmacion(null)}>
                <IonIcon slot="start" icon={refreshOutline} />
                Nueva orden de trabajo
              </IonButton>
              <IonButton expand="block" fill="outline" onClick={abrirHistorial}>
                <IonIcon slot="start" icon={timeOutline} />
                Ver historial del punto de servicio
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                color="medium"
                onClick={() => {
                  setConfirmacion(null);
                  history.push("/home");
                }}
              >
                <IonIcon slot="start" icon={homeOutline} />
                Volver al inicio
              </IonButton>
            </div>
          </div>
        ) : (
          <>
            {/* Buscador de PS */}
            {!orden && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Punto de Servicio</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonItem>
                    <IonLabel position="stacked" className="ion-text-wrap">
                      Buscar punto de servicio (solo asignados) *
                    </IonLabel>
                    <IonInput
                      placeholder="Buscar..."
                      value={psBusqueda}
                      onIonChange={(e) => setPsBusqueda(String(e.detail.value || ""))}
                    />
                  </IonItem>
                  {(psBuscando || iniciando) && <IonText color="medium"><p>Cargando…</p></IonText>}
                  {psResultados.length > 0 && !iniciando && (
                    <IonList>
                      {psResultados.map((p) => (
                        <IonItem key={p.id} button onClick={() => seleccionarPS(p)}>
                          <IonLabel>
                            <h3>{p.ps_nombre}</h3>
                            <p>{p.ps_codigo}</p>
                          </IonLabel>
                        </IonItem>
                      ))}
                    </IonList>
                  )}
                  <IonNote>
                    Al seleccionar un PS se crea (o se reanuda) la orden de trabajo. Podés salir y
                    continuarla más tarde buscando el mismo PS.
                  </IonNote>
                </IonCardContent>
              </IonCard>
            )}

            {/* Formulario de la orden */}
            {orden && catalogo && (
              <>
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle>Orden de Trabajo N° {orden.numero ?? orden.id}</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonText>
                      <p style={{ margin: "2px 0" }}>
                        <strong>{orden.ps_nombre}</strong> ({orden.ps_codigo})
                      </p>
                      {orden.fiscal_asignado_nombre ? (
                        <p style={{ margin: "2px 0" }}>Fiscal: {orden.fiscal_asignado_nombre}</p>
                      ) : null}
                      {orden.segmento_operativo ? (
                        <p style={{ margin: "2px 0" }}>Segmento: {orden.segmento_operativo}</p>
                      ) : null}
                      <p style={{ margin: "2px 0" }} className="ion-text-wrap">
                        <IonText color="medium">Inicio: {orden.creado_en || "-"}</IonText>
                      </p>
                      {finalizada && (
                        <p style={{ margin: "2px 0" }} className="ion-text-wrap">
                          <IonText color="medium">Fin: {orden.finalizado_en || "-"}</IonText>
                        </p>
                      )}
                    </IonText>

                    <IonItem>
                      <IonLabel position="stacked">Fecha del trabajo *</IonLabel>
                      <IonInput
                        type="date"
                        value={fecha}
                        disabled={finalizada}
                        onIonChange={(e) => setFecha(String(e.detail.value || ""))}
                      />
                    </IonItem>

                    <IonItem>
                      <IonLabel position="stacked">Nombre de Contacto</IonLabel>
                      <IonInput
                        placeholder="Nombre de quien recibe / firma"
                        value={nombreContacto}
                        disabled={finalizada}
                        onIonChange={(e) => setNombreContacto(String(e.detail.value || ""))}
                      />
                    </IonItem>

                    <IonItem>
                      <IonLabel position="stacked" className="ion-text-wrap">
                        Se Factura *
                      </IonLabel>
                      <IonSelect
                        value={seFactura}
                        placeholder="Elegir opción"
                        interface="action-sheet"
                        cancelText="Cancelar"
                        interfaceOptions={{ header: "Se Factura" }}
                        disabled={finalizada}
                        onIonChange={(e) => setSeFactura(String(e.detail.value || ""))}
                      >
                        {catalogo.se_factura.map((o) => (
                          <IonSelectOption key={o.codigo} value={o.codigo}>
                            {o.etiqueta}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  </IonCardContent>
                </IonCard>

                <IonCard>
                  <IonCardContent>
                    {renderCheckboxGroup(
                      "Trabajos Realizados *",
                      trabajos,
                      setTrabajos,
                      catalogo.trabajos_realizados
                    )}

                    <IonItem>
                      <IonLabel position="stacked" className="ion-text-wrap">
                        Sectores
                      </IonLabel>
                      <IonTextarea
                        autoGrow
                        rows={3}
                        value={sectores}
                        disabled={finalizada}
                        placeholder="Detalle de sectores intervenidos"
                        onIonChange={(e) => setSectores(String(e.detail.value || ""))}
                      />
                    </IonItem>

                    {renderCheckboxGroup(
                      "Frecuencia/as",
                      frecuencias,
                      setFrecuencias,
                      catalogo.frecuencias
                    )}
                  </IonCardContent>
                </IonCard>

                <IonCard>
                  <IonCardContent>
                    <IonItem>
                      <IonLabel position="stacked" className="ion-text-wrap">
                        Conformidad *
                      </IonLabel>
                      <IonSelect
                        value={conformidad}
                        placeholder="Elegir"
                        interface="action-sheet"
                        cancelText="Cancelar"
                        interfaceOptions={{ header: "Conformidad" }}
                        disabled={finalizada}
                        onIonChange={(e) => setConformidad(String(e.detail.value || ""))}
                      >
                        <IonSelectOption value="SI">Sí</IonSelectOption>
                        <IonSelectOption value="NO">No</IonSelectOption>
                      </IonSelect>
                    </IonItem>

                    <IonItem>
                      <IonLabel position="stacked" className="ion-text-wrap">
                        Observaciones
                      </IonLabel>
                      <IonTextarea
                        autoGrow
                        rows={3}
                        value={observaciones}
                        disabled={finalizada}
                        placeholder="Observaciones adicionales"
                        onIonChange={(e) => setObservaciones(String(e.detail.value || ""))}
                      />
                    </IonItem>

                    {/* Firma del cliente */}
                    <IonItem lines="none">
                      <IonLabel className="ion-text-wrap">Firma del cliente *</IonLabel>
                    </IonItem>
                    {finalizada || (modoFirma === "ver" && firmaGuardada) ? (
                      <>
                        <div style={{ textAlign: "center" }}>
                          {firmaGuardada ? (
                            <img
                              src={firmaGuardada}
                              alt="Firma del cliente"
                              style={{ maxWidth: "100%", border: "1px solid #e0e0e0", borderRadius: 8 }}
                            />
                          ) : (
                            <IonText color="medium"><p>Sin firma.</p></IonText>
                          )}
                        </div>
                        {!finalizada && (
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                            <IonButton size="small" fill="clear" onClick={volverAFirmar}>
                              Volver a firmar
                            </IonButton>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            border: "1px dashed #9e9e9e",
                            borderRadius: 8,
                            height: 180,
                            background: "#fff",
                            touchAction: "none",
                          }}
                        >
                          <canvas
                            ref={canvasRef}
                            style={{ width: "100%", height: "100%", touchAction: "none" }}
                            onPointerDown={beginDraw}
                            onPointerMove={draw}
                            onPointerUp={endDraw}
                            onPointerLeave={endDraw}
                            onPointerCancel={endDraw}
                          />
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                          <IonButton size="small" fill="clear" color="medium" onClick={limpiarFirma}>
                            Limpiar firma
                          </IonButton>
                        </div>
                      </>
                    )}
                  </IonCardContent>
                </IonCard>

                {/* Acciones */}
                {!finalizada ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
                    <IonButton expand="block" fill="outline" disabled={guardando} onClick={() => guardarBorrador(false)}>
                      <IonIcon slot="start" icon={saveOutline} />
                      {guardando ? "Guardando…" : "Guardar borrador"}
                    </IonButton>
                    <IonButton expand="block" color="success" disabled={finalizando} onClick={finalizar}>
                      <IonIcon slot="start" icon={checkmarkDoneOutline} />
                      {finalizando ? "Finalizando…" : "Finalizar orden"}
                    </IonButton>
                    <IonButton expand="block" fill="clear" color="medium" onClick={reiniciar}>
                      Cambiar de punto de servicio
                    </IonButton>
                  </div>
                ) : (
                  <div style={{ marginBottom: 24 }}>
                    <IonButton expand="block" onClick={reiniciar}>
                      <IonIcon slot="start" icon={refreshOutline} />
                      Nueva orden de trabajo
                    </IonButton>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Modal: Historial de órdenes por PS */}
        <IonModal isOpen={histOpen} onDidDismiss={() => setHistOpen(false)}>
          <IonHeader>
            <IonToolbar color="primary">
              <IonButtons slot="start">
                {histDetalle ? (
                  <IonButton onClick={() => setHistDetalle(null)}>
                    <IonIcon slot="icon-only" icon={arrowBackOutline} />
                  </IonButton>
                ) : histPsSel ? (
                  <IonButton
                    onClick={() => {
                      setHistPsSel(null);
                      setHistLista([]);
                      setHistPsBusqueda("");
                    }}
                  >
                    <IonIcon slot="icon-only" icon={arrowBackOutline} />
                  </IonButton>
                ) : null}
              </IonButtons>
              <IonTitle>Historial de Órdenes</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setHistOpen(false)}>
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {/* Paso 1: buscar PS */}
            {!histPsSel && (
              <>
                <IonItem>
                  <IonLabel position="stacked" className="ion-text-wrap">
                    Buscar punto de servicio (solo asignados)
                  </IonLabel>
                  <IonInput
                    placeholder="Buscar..."
                    value={histPsBusqueda}
                    onIonChange={(e) => setHistPsBusqueda(String(e.detail.value || ""))}
                  />
                </IonItem>
                {histBuscando && <IonText color="medium"><p>Buscando…</p></IonText>}
                {histPsResultados.length > 0 && (
                  <IonList>
                    {histPsResultados.map((p) => (
                      <IonItem key={p.id} button onClick={() => seleccionarPSHistorial(p)}>
                        <IonLabel>
                          <h3>{p.ps_nombre}</h3>
                          <p>{p.ps_codigo}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </>
            )}

            {/* Paso 2: lista de órdenes del PS */}
            {histPsSel && !histDetalle && (
              <>
                <IonText>
                  <h3 style={{ marginTop: 0 }}>{histPsSel.ps_nombre}</h3>
                  <p><IonText color="medium">{histPsSel.ps_codigo}</IonText></p>
                </IonText>
                {histCargando ? (
                  <div style={{ textAlign: "center", marginTop: 24 }}>
                    <IonSpinner name="crescent" />
                  </div>
                ) : histLista.length === 0 ? (
                  <IonText color="medium"><p>No hay órdenes cargadas para este punto de servicio.</p></IonText>
                ) : (
                  <IonList>
                    {histLista.map((h) => (
                      <IonItem key={h.id} button detail onClick={() => verDetalleHistorial(h.id)}>
                        <IonLabel className="ion-text-wrap">
                          <h3>Orden N° {h.numero ?? h.id}</h3>
                          <p>
                            Fecha: {h.fecha || "-"}
                            {h.se_factura ? ` · ${etiqueta(catalogo?.se_factura, h.se_factura)}` : ""}
                          </p>
                          <p><IonText color="medium">Inicio: {h.creado_en || "-"}</IonText></p>
                        </IonLabel>
                        <IonBadge slot="end" color={h.estado === "FINALIZADA" ? "success" : "medium"}>
                          {h.estado === "FINALIZADA" ? "Finalizada" : "Borrador"}
                        </IonBadge>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </>
            )}

            {/* Paso 3: detalle de una orden */}
            {histDetalleCargando && (
              <div style={{ textAlign: "center", marginTop: 24 }}>
                <IonSpinner name="crescent" />
              </div>
            )}
            {histDetalle && !histDetalleCargando && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Orden de Trabajo N° {histDetalle.numero ?? histDetalle.id}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <p><strong>Estado:</strong> {histDetalle.estado === "FINALIZADA" ? "Finalizada" : "Borrador"}</p>
                  <p><strong>Punto de servicio:</strong> {histDetalle.ps_nombre} ({histDetalle.ps_codigo})</p>
                  <p><strong>Fecha del trabajo:</strong> {histDetalle.fecha || "-"}</p>
                  <p><strong>Nombre de contacto:</strong> {histDetalle.nombre_contacto || "-"}</p>
                  <p><strong>Se factura:</strong> {etiqueta(catalogo?.se_factura, histDetalle.se_factura)}</p>
                  <p><strong>Trabajos realizados:</strong> {etiquetasMulti(catalogo?.trabajos_realizados, histDetalle.multi?.TRABAJOS_REALIZADOS)}</p>
                  <p><strong>Sectores:</strong> {histDetalle.sectores || "-"}</p>
                  <p><strong>Frecuencia/as:</strong> {etiquetasMulti(catalogo?.frecuencias, histDetalle.multi?.FRECUENCIAS)}</p>
                  <p><strong>Conformidad:</strong> {histDetalle.conformidad === true ? "Sí" : histDetalle.conformidad === false ? "No" : "-"}</p>
                  <p><strong>Observaciones:</strong> {histDetalle.observaciones || "-"}</p>
                  <p><IonText color="medium">Inicio: {histDetalle.creado_en || "-"}</IonText></p>
                  {histDetalle.finalizado_en && (
                    <p><IonText color="medium">Fin: {histDetalle.finalizado_en}</IonText></p>
                  )}
                  {histDetalle.firma_base64 && (
                    <>
                      <p><strong>Firma del cliente:</strong></p>
                      <img
                        src={histDetalle.firma_base64}
                        alt="Firma del cliente"
                        style={{ maxWidth: "100%", border: "1px solid #e0e0e0", borderRadius: 8 }}
                      />
                    </>
                  )}
                </IonCardContent>
              </IonCard>
            )}
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={3200}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
};

export default OrdenTrabajo;
